import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../../services/api';
import 'leaflet-geosearch/dist/geosearch.css';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import {
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Box,
    Typography,
    Container,
    IconButton,
    InputAdornment,
    Card,
    CardContent,
    Grow,
    Snackbar,
    Alert,
    LinearProgress,
    useMediaQuery,
} from '@mui/material';
import {
    ArrowForward,
    ArrowBack,
    Close as CloseIcon,
    Save as SaveIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import MaskedTextField from '../../../components/MaskedTextField';
import { jwtDecode } from 'jwt-decode';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const theme = createTheme({
    palette: {
        primary: {
            main: '#10641c',
        },
        secondary: {
            main: '#f50057',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: {
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: '1.75rem',
            '@media (max-width:600px)': {
                fontSize: '1.25rem',
            },
        },
        h6: {
            fontSize: '1.25rem',
            '@media (max-width:600px)': {
                fontSize: '1rem',
            },
        },
        subtitle1: {
            color: '#555',
            fontSize: '1rem',
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        body2: {
            fontSize: '0.875rem',
            '@media (max-width:600px)': {
                fontSize: '0.75rem',
            },
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '10px 24px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '@media (max-width:600px)': {
                        padding: '8px 16px',
                        fontSize: '0.75rem',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 0 12px rgba(16, 100, 28, 0.2)',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 16px rgba(16, 100, 28, 0.3)',
                        },
                    },
                    '@media (max-width:600px)': {
                        '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                        },
                    },
                },
            },
        },
    },
});

function MapFlyTo({ position }) {
    const map = useMap();

    useEffect(() => {
        if (position) {
            map.flyTo([position.lat, position.lng], 18, { animate: true, duration: 1 });
        }
    }, [position, map]);

    return null;
}

const formatAddress = (fullAddress) => {
    const parts = fullAddress.split(',').map((part) => part.trim());
    if (parts.length < 4) return fullAddress;
    const rua = parts[0];
    const cidade = parts[2] === 'Cedro' ? parts[2] : 'Cedro';
    const cep = parts[7] === null || '63400-000';
    return [rua, cidade, cep].filter(Boolean).join(', ');
};

function LocationMarker({ onLocationSelected, setFormData, position, setPosition, showSnackbar }) {
    useMapEvents({
        click: async (e) => {
            const { lat, lng } = e.latlng;
            onLocationSelected(e.latlng);
            setPosition({ lat, lng });

            try {
                const provider = new OpenStreetMapProvider();
                const results = await provider.search({
                    query: `${lat}, ${lng}`,
                });

                let rawAddress = results.length > 0 ? results[0].label : '';
                let address = results.length > 0 ? formatAddress(rawAddress) : 'Endereço não encontrado.';

                setFormData((prev) => ({
                    ...prev,
                    latitude: lat.toString(),
                    longitude: lng.toString(),
                    address: address,
                }));

                if (!results.length) {
                    showSnackbar('Endereço não encontrado.', 'warning');
                }
            } catch (error) {
                console.error('Erro ao buscar endereço:', error);
                setFormData((prev) => ({
                    ...prev,
                    latitude: lat.toString(),
                    longitude: lng.toString(),
                    address: 'Erro ao buscar endereço',
                }));
                showSnackbar('Erro ao buscar endereço', 'error');
            }
        },
    });

    return position ? <Marker position={position} /> : null;
}

function EntrepreneurRegistration() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [typeUser, setTypeUser] = useState(null);
    const [creatorId, setCreatorId] = useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);
    const [coords, setCoords] = useState(null);
    const [position, setPosition] = useState(null);
    const [isEditMode, setIsEditMode] = useState(!!id);
    const [initialFormData, setInitialFormData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [formData, setFormData] = useState({
        companyName: '',
        cpf_cnpj: '',
        businessSizeId: '',
        startDate: '',
        responsibleName: '',
        phone: '',
        email: '',
        address: '',
        referencePoint: '',
        area: '',
        latitude: '',
        longitude: '',
        businessType: [],
        hasLicense: true,
        active: 1,
        neighborhoodId: '',
        creatorId: null,
        observation: '',
        number: '',
        visible: false
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [businessSizes, setBusinessSizes] = useState([]);
    const [loadingBusinessSizes, setLoadingBusinessSizes] = useState(true);
    const [errorBusinessSizes, setErrorBusinessSizes] = useState(null);
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [filteredNeighborhoods, setFilteredNeighborhoods] = useState([]);
    const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(true);
    const [errorNeighborhoods, setErrorNeighborhoods] = useState(null);
    const [economicActivities, setEconomicActivities] = useState([]);
    const [loadingEntrepreneur, setLoadingEntrepreneur] = useState(!!id);
    const [searchQuery, setSearchQuery] = useState('');

    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    };

    const steps = [
        { label: 'Empresa', icon: <BusinessIcon /> },
        { label: 'Responsável', icon: <PersonIcon /> },
        { label: 'Localização', icon: <LocationOnIcon /> },
        { label: 'Características', icon: <WorkIcon /> },
    ];

    const isStep0Valid = () => {
        return formData.companyName.trim() !== '' && formData.businessSizeId !== '';
    };

    const isStep1Valid = () => {
        return formData.responsibleName.trim() !== '';
    };

    const isStep2Valid = () => {
        return (
            formData.address.trim() !== '' &&
            formData.area !== '' &&
            formData.neighborhoodId !== '' &&
            formData.number.trim() !== ''
        );
    };

    const isStep3Valid = () => {
        return formData.businessType.length > 0;
    };

    const hasFormChanged = () => {
        if (!initialFormData) return false;
        return (
            formData.companyName !== initialFormData.companyName ||
            formData.cpf_cnpj !== initialFormData.cpf_cnpj ||
            formData.businessSizeId !== initialFormData.businessSizeId ||
            formData.startDate !== initialFormData.startDate ||
            formData.responsibleName !== initialFormData.responsibleName ||
            formData.phone !== initialFormData.phone ||
            formData.email !== initialFormData.email ||
            formData.address !== initialFormData.address ||
            formData.referencePoint !== initialFormData.referencePoint ||
            formData.area !== initialFormData.area ||
            formData.latitude !== initialFormData.latitude ||
            formData.longitude !== initialFormData.longitude ||
            formData.hasLicense !== initialFormData.hasLicense ||
            formData.active !== initialFormData.active ||
            formData.neighborhoodId !== initialFormData.neighborhoodId ||
            formData.observation !== initialFormData.observation ||
            formData.number !== initialFormData.number ||
            formData.visible !== initialFormData.visible ||
            JSON.stringify(formData.businessType.sort()) !== JSON.stringify(initialFormData.businessType.sort())
        );
    };

    useEffect(() => {
        const path = location.pathname.split('/');
        setTypeUser(path[1]);
        const decode = jwtDecode(localStorage.getItem('token'));
        setCreatorId(decode.id);

        const fetchEntrepreneur = async () => {
            if (id) {
                try {
                    setLoadingEntrepreneur(true);
                    const response = await api.get(`/economic-agents/${id}`);
                    const data = response.data;
                    const formData = {
                        companyName: data.name || '',
                        cpf_cnpj: data.cpf_cnpj || '',
                        businessSizeId: data.businessSize?.id || '',
                        startDate: data.startDate ? data.startDate.split('T')[0] : '',
                        responsibleName: data.user?.name || '',
                        phone: data.phone || '',
                        email: data.user?.email || '',
                        address: data.address || '',
                        referencePoint: data.referencePoint || '',
                        area: data.neighborhood?.zone || '',
                        latitude: data.location?.coordinates[1]?.toString() || '',
                        longitude: data.location?.coordinates[0]?.toString() || '',
                        businessType: data.typeBusiness?.map((tb) => tb.id) || [],
                        hasLicense: data.hasLicense ?? true,
                        active: data.active || 1,
                        neighborhoodId: data.neighborhood?.id || '',
                        creatorId: data.creator?.id || creatorId,
                        observation: data.observation || '',
                        number: data.number || '',
                        visible: data.visible || false,
                    };
                    setFormData(formData);
                    setInitialFormData(formData);
                    if (data.location?.coordinates[0] && data.location?.coordinates[1]) {
                        setPosition({ lat: data.location.coordinates[1], lng: data.location.coordinates[0] });
                        setCoords({ lat: data.location.coordinates[1], lng: data.location.coordinates[0] });
                    }
                } catch (error) {
                    showSnackbar('Erro ao carregar dados do empreendedor', 'error');
                    console.error('Error fetching entrepreneur:', error);
                } finally {
                    setLoadingEntrepreneur(false);
                }
            }
        };

        const fetchBusinessSizes = async () => {
            setLoadingBusinessSizes(true);
            try {
                const response = await api.get('/business-size');
                const sortedSizes = response.data
                    .map((size) => ({ id: size.id, name: size.name || size.nome }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setBusinessSizes(sortedSizes);
            } catch (error) {
                setErrorBusinessSizes(error.message);
                showSnackbar('Erro ao carregar portes de negócio', 'error');
            } finally {
                setLoadingBusinessSizes(false);
            }
        };

        const fetchNeighborhoods = async () => {
            setLoadingNeighborhoods(true);
            try {
                const response = await api.get('/neighborhood');
                const sortedNeighborhoods = response.data
                    .map((neighborhood) => ({
                        id: neighborhood.id,
                        name: neighborhood.name || neighborhood.nome,
                        zone: neighborhood.zone,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setNeighborhoods(sortedNeighborhoods);
            } catch (error) {
                setErrorNeighborhoods(error.message);
                showSnackbar('Erro ao carregar bairros', 'error');
            } finally {
                setLoadingNeighborhoods(false);
            }
        };

        const fetchEconomicActivities = async () => {
            try {
                const response = await api.get('/type-business');
                setEconomicActivities(response.data.map((activity) => ({
                    id: activity.id,
                    name: activity.name || activity.nome,
                })));
            } catch (error) {
                showSnackbar('Erro ao carregar atividades econômicas', 'error');
            }
        };

        fetchEntrepreneur();
        fetchBusinessSizes();
        fetchNeighborhoods();
        fetchEconomicActivities();
    }, [location.pathname, id, creatorId]);

    useEffect(() => {
        if (formData.area) {
            const filtered = neighborhoods.filter((n) => n.zone === formData.area);
            setFilteredNeighborhoods(filtered);
        } else {
            setFilteredNeighborhoods([]);
        }
    }, [formData.area, neighborhoods]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'businessType' && type === 'checkbox') {
            setFormData((prev) => {
                const updatedBusinessTypes = checked
                    ? [...prev.businessType, value]
                    : prev.businessType.filter((id) => id !== value);
                return { ...prev, businessType: updatedBusinessTypes };
            });
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
                ...(name === 'area' ? { neighborhoodId: '' } : {}),
            }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        const { latitude, longitude, ...restFormData } = formData;
        const submissionData = {
            ...restFormData,
            location: latitude && longitude ? { lon: longitude, lat: latitude } : null,
            name: formData.companyName,
            creatorId: formData.creatorId || creatorId,
            email: formData.email || null,
            cpf_cnpj: formData.cpf_cnpj || null,
            observation: formData.observation || null,
            referencePoint: formData.referencePoint || null,
            startDate: formData.startDate || null,
            number: formData.number || null,
        };

        try {
            if (isEditMode) {
                const response = await api.put(`/economic-agents/${id}`, submissionData);
                if (response.status === 200) {
                    showSnackbar('Empreendedor atualizado com sucesso.', 'success');
                    setTimeout(() => {
                        navigate(`/${typeUser}/DetailsEntrepreneur/${id}`, { replace: true });
                    }, 2000);
                }
            } else {
                const response = await api.post('/economic-agents/', submissionData);
                if (response.status === 201) {
                    setFormData({
                        companyName: '',
                        cpf_cnpj: '',
                        businessSizeId: '',
                        startDate: '',
                        responsibleName: '',
                        phone: '',
                        email: '',
                        address: '',
                        referencePoint: '',
                        area: '',
                        latitude: '',
                        longitude: '',
                        businessType: [],
                        hasLicense: true,
                        active: 1,
                        neighborhoodId: '',
                        creatorId: null,
                        observation: '',
                        number: '',
                    });
                    setInitialFormData(null);
                    setPosition(null);
                    setActiveStep(0);
                    showSnackbar('Empreendedor cadastrado com sucesso.', 'success');
                    setTimeout(() => {
                        navigate(`/${typeUser}/DetailsEntrepreneur/${response.data.economicAgent.id}`);
                    }, 2000);
                }
            }
        } catch (error) {
            const message = error.response?.data?.error || (isEditMode ? 'Erro ao atualizar empreendedor' : 'Erro ao cadastrar empreendedor');
            showSnackbar(message, 'error');
            console.error(isEditMode ? 'Erro ao atualizar empreendedor:' : 'Erro ao cadastrar empreendedor:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleCancel = () => {
        setFormData({
            companyName: '',
            cpf_cnpj: '',
            businessSizeId: '',
            startDate: '',
            responsibleName: '',
            phone: '',
            email: '',
            address: '',
            referencePoint: '',
            area: '',
            latitude: '',
            longitude: '',
            businessType: [],
            hasLicense: true,
            active: 1,
            neighborhoodId: '',
            creatorId: null,
            observation: '',
            number: '',
        });
        setInitialFormData(null);
        setPosition(null);
        setActiveStep(0);
        showSnackbar(isEditMode ? 'Edição cancelada.' : 'Cadastro cancelado.', 'info');
        navigate(`/${typeUser}/entrepreneur`);
    };

    const handleSearchAddress = async () => {
        const provider = new OpenStreetMapProvider();
        try {
            const results = await provider.search({ query: formData.address + ',cedro,ceará' });

            if (results.length > 0) {
                const { y: lat, x: lng, label } = results[0];
                const formatted = formatAddress(label);

                setCoords({ lat, lng });
                setPosition({ lat, lng });
                setFormData((prev) => ({
                    ...prev,
                    latitude: lat.toString(),
                    longitude: lng.toString(),
                    address: formatted || 'Rua não encontrada',
                }));

                showSnackbar('Endereço encontrado com sucesso.', 'success');
            } else {
                showSnackbar('Endereço não encontrado.', 'warning');
            }
        } catch (error) {
            console.error('Erro ao buscar endereço:', error);
            showSnackbar('Erro ao buscar endereço', 'error');
        }
    };

    const handleGetCurrentLocation = async () => {
        if (!navigator.geolocation) {
            showSnackbar('Geolocalização não é suportada pelo seu navegador.', 'error');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setCoords({ lat: latitude, lng: longitude });
                setPosition({ lat: latitude, lng: longitude });

                try {
                    const provider = new OpenStreetMapProvider();
                    const results = await provider.search({
                        query: `${latitude}, ${longitude}`,
                    });

                    let rawAddress = results.length > 0 ? results[0].label : '';
                    let address = results.length > 0 ? formatAddress(rawAddress) : 'Endereço não encontrado.';

                    setFormData((prev) => ({
                        ...prev,
                        latitude: latitude.toString(),
                        longitude: longitude.toString(),
                        address: address,
                    }));

                    if (results.length > 0) {
                        showSnackbar('Localização atual encontrada com sucesso.', 'success');
                    } else {
                        showSnackbar('Endereço não encontrado para a localização atual.', 'warning');
                    }
                } catch (error) {
                    console.error('Erro ao buscar endereço:', error);
                    setFormData((prev) => ({
                        ...prev,
                        latitude: latitude.toString(),
                        longitude: longitude.toString(),
                        address: 'Erro ao buscar endereço',
                    }));
                    showSnackbar('Erro ao buscar endereço', 'error');
                }
            },
            (error) => {
                console.error('Erro ao obter localização:', error);
                if (error.code === error.PERMISSION_DENIED) {
                    showSnackbar('Permissão para geolocalização foi negada.', 'error');
                } else if (error.code === error.POSITION_UNAVAILABLE) {
                    showSnackbar('Localização não disponível.', 'error');
                } else if (error.code === error.TIMEOUT) {
                    showSnackbar('Tempo esgotado para obter localização.', 'error');
                } else {
                    showSnackbar('Erro ao obter localização.', 'error');
                }
            }
        );
    };

    const renderStepContent = (step) => {
        if (loadingEntrepreneur) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                    <CircularProgress size={isMobile ? 30 : 40} sx={{ color: '#10641c' }} />
                </Box>
            );
        }

        switch (step) {
            case 0:
                return (
                    <Grow in={activeStep === 0} timeout={600}>
                        <Box sx={{ p: isMobile ? 2 : 2, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>
                                Informações da Empresa
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Preencha os dados da empresa para começar.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <TextField
                                        fullWidth
                                        label="Nome/Razão Social (Obrigatório)"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        margin="normal"
                                        variant="outlined"
                                    />
                                </Box>
                                <MaskedTextField
                                    id="cpf_cnpj"
                                    name="cpf_cnpj"
                                    value={formData.cpf_cnpj}
                                    onChange={handleChange}
                                    margin="normal"
                                    mask={
                                        formData.cpf_cnpj.length > 11
                                            ? '99.999.999/9999-99'
                                            : '999.999.999-99'
                                    }
                                    label="CPF/CNPJ (Opcional)"
                                />
                                <FormControl margin="normal" variant="outlined">
                                    <InputLabel>Porte (Obrigatório)</InputLabel>
                                    <Select
                                        name="businessSizeId"
                                        value={formData.businessSizeId}
                                        onChange={handleChange}
                                        label="Porte (Obrigatório)"
                                        disabled={loadingBusinessSizes}
                                        sx={{ borderRadius: '12px', backgroundColor: '#fff' }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 160,
                                                    width: 'auto',
                                                    maxWidth: '200px',
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            style: {
                                                marginTop: isMobile ? '5px' : '0px',
                                            },
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) return 'Selecione o porte';
                                            const selectedSize = businessSizes.find((size) => size.id === selected)?.name || '';
                                            return selectedSize.length > 20 ? `${selectedSize.substring(0, 20)}...` : selectedSize;
                                        }}
                                    >
                                        {loadingBusinessSizes && (
                                            <MenuItem disabled>
                                                <CircularProgress size={20} />
                                            </MenuItem>
                                        )}
                                        {errorBusinessSizes && (
                                            <MenuItem disabled>{`Erro: ${errorBusinessSizes}`}</MenuItem>
                                        )}
                                        {businessSizes.map((size) => (
                                            <MenuItem key={size.id} value={size.id}>
                                                <ListItemText
                                                    primary={size.name}
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word',
                                                        maxWidth: '100%',
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Início das Atividades (Opcional)"
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    margin="normal"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        '@media (max-width: 600px)': {
                                            '&:focus-within': {
                                                position: 'relative',
                                                zIndex: 1,
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        inputProps: {
                                            max: new Date().toISOString().split('T')[0],
                                        },
                                        onFocus: (e) => {
                                            if (window.innerWidth <= 600) {
                                                e.target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                                            }
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCancel}
                                    color="secondary"
                                    startIcon={<CloseIcon />}
                                    sx={{
                                        bgcolor: 'red',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#cc0000' },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    color="primary"
                                    endIcon={<ArrowForward />}
                                    disabled={!isStep0Valid()}
                                    sx={{
                                        bgcolor: '#10641c',
                                        '&:hover': { bgcolor: '#0a3d0f' },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Próximo
                                </Button>
                            </Box>
                        </Box>
                    </Grow>
                );
            case 1:
                return (
                    <Grow in={activeStep === 1} timeout={600}>
                        <Box sx={{ p: isMobile ? 2 : 2, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>
                                Informações do Responsável
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Insira os dados do responsável pela empresa.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <TextField
                                        fullWidth
                                        label="Nome Completo (Obrigatório)"
                                        name="responsibleName"
                                        value={formData.responsibleName}
                                        onChange={handleChange}
                                        margin="normal"
                                        variant="outlined"
                                    />
                                </Box>
                                <MaskedTextField
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    mask={
                                        formData.phone.length <= 10
                                            ? '(99) 9999-9999'
                                            : '(99) 9 9999-9999'
                                    }
                                    label="Telefone/WhatsApp"
                                    margin="normal"
                                />
                                <TextField
                                    label="E-mail (Opcional)"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    margin="normal"
                                    variant="outlined"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    startIcon={<ArrowBack />}
                                    sx={{
                                        borderColor: 'grey.500',
                                        color: 'grey.700',
                                        '&:hover': {
                                            borderColor: 'grey.700',
                                            backgroundColor: 'grey.100',
                                        },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Voltar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    color="primary"
                                    endIcon={<ArrowForward />}
                                    disabled={!isStep1Valid()}
                                    sx={{
                                        bgcolor: '#10641c',
                                        '&:hover': { bgcolor: '#0a3d0f' },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Próximo
                                </Button>
                            </Box>
                        </Box>
                    </Grow>
                );
            case 2:
                return (
                    <Grow in={activeStep === 2} timeout={600}>
                        <Box sx={{ p: isMobile ? 2 : 2, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>
                                Localização do Empreendimento
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Você pode informar o endereço no campo 'Endereço', digitar as coordenadas ou selecionar a localização diretamente no mapa.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        gridColumn: '1 / -1',
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        alignItems: isMobile ? 'stretch' : 'center',
                                        gap: isMobile ? 1 : 1,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Endereço (Obrigatório)"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        variant="outlined"
                                        placeholder="Buscar endereço..."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocationOnIcon color="primary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleSearchAddress}
                                        startIcon={<SearchIcon />}
                                        sx={{
                                            bgcolor: '#10641c',
                                            color: '#fff',
                                            '&:hover': { bgcolor: '#0e4e16' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            mt: isMobile ? 1 : 0,
                                            width: isMobile ? '100%' : '13%',
                                            height: isMobile ? 35 : 50,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        aria-label="Buscar endereço no mapa"
                                    >
                                        Buscar
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        gridColumn: '1 / -1',
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : '2fr 2fr 3fr',
                                        gap: 2,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Número (Obrigatório)"
                                        name="number"
                                        value={formData.number || ''}
                                        onChange={handleChange}
                                        margin="normal"
                                        variant="outlined"
                                    />
                                    <FormControl margin="normal" variant="outlined" fullWidth>
                                        <InputLabel>Zona (Obrigatório)</InputLabel>
                                        <Select
                                            name="area"
                                            value={formData.area}
                                            onChange={handleChange}
                                            label="Zona (Obrigatório)"
                                            sx={{ borderRadius: '12px', backgroundColor: '#fff' }}
                                        >
                                            <MenuItem value="urbana">Urbana</MenuItem>
                                            <MenuItem value="rural">Rural</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl margin="normal" variant="outlined" fullWidth>
                                        <InputLabel>Bairro (Obrigatório)</InputLabel>
                                        <Select
                                            name="neighborhoodId"
                                            value={formData.neighborhoodId}
                                            onChange={handleChange}
                                            label="Bairro (Obrigatório)"
                                            disabled={!formData.area}
                                            sx={{ borderRadius: '12px', backgroundColor: '#fff' }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 200,
                                                        width: 'auto',
                                                        maxWidth: '200px',
                                                    },
                                                },
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                disableScrollLock: true,
                                                style: {
                                                    marginTop: isMobile ? '5px' : '0px',
                                                },
                                            }}
                                            renderValue={(selected) => {
                                                if (!selected) return 'Selecione o bairro';
                                                const selectedNeighborhood = filteredNeighborhoods.find((n) => n.id === selected)?.name || '';
                                                return selectedNeighborhood.length > 20 ? `${selectedNeighborhood.substring(0, 25)}...` : selectedNeighborhood;
                                            }}
                                        >
                                            {filteredNeighborhoods.map((neighborhood) => (
                                                <MenuItem key={neighborhood.id} value={neighborhood.id}>
                                                    <ListItemText
                                                        primary={neighborhood.name}
                                                        sx={{
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word',
                                                            maxWidth: '100%',
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <TextField
                                        fullWidth
                                        label="Ponto de Referência (Opcional)"
                                        name="referencePoint"
                                        value={formData.referencePoint}
                                        onChange={handleChange}
                                        margin="normal"
                                        variant="outlined"
                                    />
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                        Informe as Coordenadas
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1.3fr auto',
                                            gap: 1,
                                            width: '100%',
                                            alignItems: isMobile ? 'stretch' : 'center',
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            label="Latitude"
                                            name="latitude"
                                            value={formData.latitude}
                                            onChange={handleChange}
                                            margin="normal"
                                            variant="outlined"
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                gap: isMobile ? 1 : 2,
                                                alignItems: isMobile ? 'stretch' : 'center',
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label="Longitude"
                                                name="longitude"
                                                value={formData.longitude}
                                                onChange={handleChange}
                                                margin="normal"
                                                variant="outlined"
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={async () => {
                                                    const lat = parseFloat(formData.latitude);
                                                    const lng = parseFloat(formData.longitude);
                                                    if (!isNaN(lat) && !isNaN(lng)) {
                                                        setCoords({ lat, lng });
                                                        setPosition({ lat, lng });

                                                        try {
                                                            const provider = new OpenStreetMapProvider();
                                                            const results = await provider.search({
                                                                query: `${lat}, ${lng}`,
                                                            });

                                                            let rawAddress = results.length > 0 ? results[0].label : '';
                                                            let address = results.length > 0 ? formatAddress(rawAddress) : 'Endereço não encontrado.';

                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                latitude: lat.toString(),
                                                                longitude: lng.toString(),
                                                                address: address,
                                                            }));

                                                            if (results.length > 0) {
                                                                showSnackbar('Localização e endereço encontrados com sucesso.', 'success');
                                                            } else {
                                                                showSnackbar('Endereço não encontrado para as coordenadas fornecidas.', 'warning');
                                                            }
                                                        } catch (error) {
                                                            console.error('Erro ao buscar endereço:', error);
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                latitude: lat.toString(),
                                                                longitude: lng.toString(),
                                                                address: 'Erro ao buscar endereço',
                                                            }));
                                                            showSnackbar('Erro ao buscar endereço', 'error');
                                                        }
                                                    } else {
                                                        showSnackbar('Coordenadas inválidas.', 'error');
                                                    }
                                                }}
                                                startIcon={<SearchIcon />}
                                                sx={{
                                                    bgcolor: '#10641c',
                                                    color: '#fff',
                                                    '&:hover': { bgcolor: '#0e4e16' },
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    mt: isMobile ? 1 : 0,
                                                    width: isMobile ? '100%' : '29%',
                                                    height: isMobile ? 35 : 50,
                                                    marginTop: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                aria-label="Buscar coordenadas no mapa"
                                            >
                                                Buscar
                                            </Button>
                                        </Box>
                                    </Box>
                                    {/*<Button
                                        variant="contained"
                                        onClick={handleGetCurrentLocation}
                                        startIcon={<LocationOnIcon />}
                                        sx={{
                                            bgcolor: '#10641c',
                                            color: '#fff',
                                            '&:hover': { bgcolor: '#0e4e16' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            mt: 1,
                                            width: isMobile ? '100%' : '29%',
                                            height: isMobile ? 35 : 50,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        aria-label="Localização atual"
                                    >
                                        Localização Atual
                                    </Button>*/}
                                </Box>
                            </Box>
                            <Typography variant="h6" gutterBottom mt={3} fontWeight={600}>
                                Selecione o local do Empreendimento
                            </Typography>
                            <MapContainer
                                center={[-6.6000, -39.0600]}
                                zoom={15}
                                style={{
                                    height: isMobile ? '250px' : '400px',
                                    width: '100%',
                                    zIndex: 0,
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <LocationMarker
                                    onLocationSelected={(latlng) => setCoords(latlng)}
                                    setFormData={setFormData}
                                    position={position}
                                    setPosition={setPosition}
                                    showSnackbar={showSnackbar}
                                />
                                <MapFlyTo position={position} />
                            </MapContainer>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    startIcon={<ArrowBack />}
                                    sx={{
                                        borderColor: 'grey.500',
                                        color: 'grey.700',
                                        '&:hover': {
                                            borderColor: 'grey.700',
                                            backgroundColor: 'grey.100',
                                        },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Voltar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    color="primary"
                                    endIcon={<ArrowForward />}
                                    disabled={!isStep2Valid()}
                                    sx={{
                                        bgcolor: '#10641c',
                                        '&:hover': { bgcolor: '#0a3d0f' },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Próximo
                                </Button>
                            </Box>
                        </Box>
                    </Grow>
                );
            case 3:
                const filteredActivities = economicActivities
                    .filter((activity) =>
                        normalizeText(activity.name).includes(normalizeText(searchQuery))
                    )
                    .sort((a, b) => a.name.localeCompare(b.name));

                return (
                    <Grow in={activeStep === 3} timeout={600}>
                        <Box sx={{ p: isMobile ? 2 : 2, width: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>
                                Características do Negócio
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Selecione as atividades econômicas do empreendedor e informe se possui alvará.
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Tipos de Negócio
                            </Typography>
                            <FormControl fullWidth margin="normal" variant="outlined">
                                <InputLabel>Tipos de Negócio (Obrigatório)</InputLabel>
                                <Select
                                    multiple
                                    open={isDropdownOpen}
                                    onOpen={() => setIsDropdownOpen(true)}
                                    onClose={() => {
                                        setIsDropdownOpen(false);
                                        setSearchQuery('');
                                    }}
                                    value={formData.businessType}
                                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                                    renderValue={(selected) =>
                                        selected.length === 0
                                            ? "Selecione os tipos de negócio"
                                            : economicActivities
                                                .filter((activity) => selected.includes(activity.id))
                                                .map((activity) => activity.name)
                                                .join(', ')
                                    }
                                    label="Tipos de Negócio (Obrigatório)"
                                    sx={{ borderRadius: '12px', backgroundColor: '#fff' }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                                width: 'auto',
                                                maxWidth: '60vw',
                                            },
                                        },
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        disableScrollLock: true,
                                        style: {
                                            marginTop: isMobile ? '5px' : '0px',
                                            maxHeight: 'calc(100vh - 200px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 1,
                                            position: 'sticky',
                                            top: 0,
                                            bgcolor: '#fff',
                                            zIndex: 1,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <TextField
                                            fullWidth
                                            placeholder="Buscar tipos de negócio..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            autoFocus
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                    setIsDropdownOpen(false);
                                                    setSearchQuery('');
                                                }
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Box>
                                    {filteredActivities.length === 0 ? (
                                        <MenuItem disabled>
                                            <ListItemText primary="Tipo de negócio não encontrado" />
                                        </MenuItem>
                                    ) : (
                                        filteredActivities.map((activity) => (
                                            <MenuItem key={activity.id} value={activity.id}>
                                                <Checkbox checked={formData.businessType.includes(activity.id)} />
                                                <ListItemText
                                                    primary={activity.name}
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word',
                                                        maxWidth: '100%',
                                                    }}
                                                />
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <FormControl component="fieldset" margin="normal">
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Possui alvará de funcionamento?
                                </Typography>
                                <RadioGroup
                                    row
                                    name="hasLicense"
                                    value={formData.hasLicense}
                                    onChange={(e) => setFormData({ ...formData, hasLicense: e.target.value === 'true' })}
                                >
                                    <FormControlLabel value={true} control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value={false} control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                            </FormControl>
                            <FormControl component="fieldset" margin="normal">
                                <Typography variant="subtitle1" fontWeight={600}>
                                    O empreendedor deseja que seu negócio apareça no mapa da cidade?
                                </Typography>
                                <RadioGroup
                                    row
                                    name="visible"
                                    value={formData.visible}
                                    onChange={(e) => setFormData({ ...formData, visible: e.target.value === 'true' })}
                                >
                                    <FormControlLabel value={true} control={<Radio color="primary" />} label="Sim" />
                                    <FormControlLabel value={false} control={<Radio color="primary" />} label="Não" />
                                </RadioGroup>
                            </FormControl>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <TextField
                                    fullWidth
                                    label="Observações (Opcional)"
                                    name="observation"
                                    value={formData.observation}
                                    onChange={handleChange}
                                    margin="normal"
                                    variant="outlined"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    startIcon={<ArrowBack />}
                                    sx={{
                                        borderColor: 'grey.500',
                                        color: 'grey.700',
                                        '&:hover': {
                                            borderColor: 'grey.700',
                                            backgroundColor: 'grey.100',
                                        },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    Voltar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    startIcon={<SaveIcon />}
                                    color="primary"
                                    disabled={!isStep3Valid() || (isEditMode && !hasFormChanged()) || isSubmitting}
                                    sx={{
                                        bgcolor: '#10641c',
                                        '&:hover': { bgcolor: '#0a3d0f' },
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                                    ) : isEditMode ? (
                                        'Salvar'
                                    ) : (
                                        'Cadastrar'
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Grow>
                );
            default:
                return null;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container
                maxWidth={false}
                sx={{
                    my: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    px: isMobile ? 1 : 2,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <Card
                    elevation={2}
                    sx={{
                        width: isMobile ? '100%' : '70%',
                        maxWidth: isMobile ? '100%' : '900px',
                        borderRadius: '15px',
                        background: 'linear-gradient(145deg, #ffffff, #fafafa)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        p: isMobile ? 2 : 4,
                        boxSizing: 'border-box',
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={(activeStep / (steps.length - 1)) * 100}
                        sx={{
                            mb: 4,
                            height: 5,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#10641c',
                            },
                        }}
                    />
                    <Typography
                        variant="h5"
                        gutterBottom
                        align="center"
                        sx={{
                            fontWeight: 700,
                            color: '#10641c',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            mb: 5,
                            fontSize: isMobile ? '1.5rem' : '2rem',
                        }}
                    >
                        {isEditMode ? 'Editar Empreendedor' : 'Cadastro do Empreendedor'}
                    </Typography>
                    <Box sx={{ mb: 0, width: '100%' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
                                gap: 2,
                                justifyItems: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {steps.map((step, index) => (
                                <Box
                                    key={step.label}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: activeStep >= index ? 'primary.main' : 'grey.300',
                                            color: '#fff',
                                            borderRadius: '50%',
                                            width: isMobile ? 32 : 40,
                                            height: isMobile ? 32 : 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: activeStep >= index ? '0 0 12px rgba(16, 100, 28, 0.4)' : 'none',
                                            transition: 'all 0.3s ease',
                                            mb: 1,
                                        }}
                                    >
                                        {step.icon}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: activeStep >= index ? 700 : 500,
                                            color: activeStep >= index ? 'primary.main' : 'grey.500',
                                            fontSize: isMobile ? '0.75rem' : '1rem',
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <CardContent sx={{ width: '100%', boxSizing: 'border-box' }}>
                        {renderStepContent(activeStep)}
                    </CardContent>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Card>
            </Container>
        </ThemeProvider>
    );
}

export default EntrepreneurRegistration;